/**
 * Workspaces Browser
 * 
 * Super admin page for managing all workspaces:
 * - List/filter/search workspaces
 * - Change status/plan
 * - Create demo workspaces
 */

import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Building2,
  Search,
  Plus,
  MoreVertical,
  Users,
  FileText,
  RefreshCcw,
  Trash2,
  PauseCircle,
  PlayCircle,
  Sparkles,
  ChevronDown,
} from 'lucide-react';
import { listAllWorkspaces, updateWorkspaceStatus, type WorkspaceOverview, type WorkspacePlan, type WorkspaceStatus } from '../../lib/superAdminService';

// ============================================================================
// Filter Types
// ============================================================================

type FilterKey = 'status' | 'plan' | 'demo';

interface Filters {
  status?: WorkspaceStatus;
  plan?: WorkspacePlan;
  demo?: boolean;
}

// ============================================================================
// Components
// ============================================================================

function WorkspaceCard({ workspace, onAction }: { 
  workspace: WorkspaceOverview;
  onAction: (action: string, ws: WorkspaceOverview) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  
  const statusColors: Record<string, string> = {
    active: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
    demo: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
    suspended: 'bg-[var(--coral-dim)] text-[var(--coral)]',
    // bg-[var(--hover-tint)] maps to var(--surface) in dark mode (same as card bg → invisible).
    // bg-[var(--hover-tint)] maps to rgba(255,255,255,0.10) — subtly distinct in dark mode.
    archived: 'bg-[var(--hover-tint)] text-[var(--text-sec)]',
  };
  
  const planColors: Record<string, string> = {
    // Same reason as archived: gray-100 disappears in dark mode.
    free: 'bg-[var(--hover-tint)] text-[var(--text-sec)]',
    starter: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
    professional: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
    enterprise: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  };
  
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-[var(--rule)] hover:shadow-md transition-all">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-4">
          <div className={`p-3  ${workspace.isDemo ? 'bg-[var(--mustard-dim)]' : 'bg-[var(--hover-tint)]'}`}>
            {workspace.isDemo ? (
              <Sparkles className="h-6 w-6 text-[var(--mustard)]" />
            ) : (
              <Building2 className="h-6 w-6 text-[var(--text-sec)]" />
            )}
          </div>
          <div>
            <h3 className="font-semibold text-[var(--text-pri)]">{workspace.name}</h3>
            <p className="text-sm text-[var(--text-ter)]">{workspace.slug}</p>
          </div>
        </div>
        
        {/* Actions Menu */}
        <div className="relative">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            <MoreVertical className="h-5 w-5" />
          </button>
          
          {menuOpen && (
            <>
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setMenuOpen(false)} 
              />
              <div className="absolute right-0 top-full mt-1 w-48 bg-[var(--bg-raised)] border border-[var(--rule)] shadow-lg z-20 py-1">
                {workspace.status === 'active' ? (
                  <button
                    onClick={() => { onAction('suspend', workspace); setMenuOpen(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-[var(--mustard)] hover:bg-[var(--mustard-dim)] flex items-center gap-2"
                  >
                    <PauseCircle className="h-4 w-4" />
                    Suspend
                  </button>
                ) : workspace.status === 'suspended' ? (
                  <button
                    onClick={() => { onAction('activate', workspace); setMenuOpen(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)] flex items-center gap-2"
                  >
                    <PlayCircle className="h-4 w-4" />
                    Activate
                  </button>
                ) : null}
                <button
                  onClick={() => { onAction('delete', workspace); setMenuOpen(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Badges */}
      <div className="flex flex-wrap gap-2 mb-4">
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[workspace.status] || 'bg-[var(--hover-tint)] text-[var(--text-sec)]'}`}>
          {workspace.status || 'unknown'}
        </span>
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${planColors[workspace.plan] || 'bg-[var(--hover-tint)] text-[var(--text-sec)]'}`}>
          {workspace.plan || 'unknown'}
        </span>
      </div>
      
      {/* Description */}
      {workspace.description && (
        <p className="text-sm text-[var(--text-sec)] mb-4 line-clamp-2">{workspace.description}</p>
      )}
      
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 py-4 border-t border-[var(--rule)]">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-[var(--text-ter)] mb-1">
            <Users className="h-4 w-4" />
          </div>
          <p className="text-lg font-semibold text-[var(--text-pri)]">{workspace.memberCount}</p>
          <p className="text-xs text-[var(--text-ter)]">Members</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-[var(--text-ter)] mb-1">
            <FileText className="h-4 w-4" />
          </div>
          <p className="text-lg font-semibold text-[var(--text-pri)]">{workspace.candidateCount}</p>
          <p className="text-xs text-[var(--text-ter)]">Items</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-[var(--text-ter)] mb-1">
            <RefreshCcw className="h-4 w-4" />
          </div>
          <p className="text-lg font-semibold text-[var(--text-pri)]">{workspace.processCount}</p>
          <p className="text-xs text-[var(--text-ter)]">Cycles</p>
        </div>
      </div>
      
      {/* Footer */}
      <div className="pt-4 border-t border-[var(--rule)] text-sm text-[var(--text-ter)]">
        <p>Owner: {workspace.ownerEmail}</p>
        <p>Created: {new Date(workspace.createdAt).toLocaleDateString()}</p>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function WorkspacesBrowser() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [workspaces, setWorkspaces] = useState<WorkspaceOverview[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<Filters>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadData = async () => {
    setLoadError(null);
    setLoading(true);
    try {
      const data = await listAllWorkspaces();
      setWorkspaces(data);
    } catch (e) {
      console.error(e);
      setLoadError(e instanceof Error ? e.message : 'Failed to load workspaces');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Parse initial filters from URL
  useEffect(() => {
    const urlFilters: Filters = {};
    if (searchParams.get('status')) urlFilters.status = searchParams.get('status') as WorkspaceStatus;
    if (searchParams.get('plan')) urlFilters.plan = searchParams.get('plan') as WorkspacePlan;
    if (searchParams.get('filter') === 'demo') urlFilters.demo = true;
    setFilters(urlFilters);
  }, [searchParams]);
  
  // Filter workspaces
  const filteredWorkspaces = useMemo(() => {
    return workspaces.filter(ws => {
      // Search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!ws.name.toLowerCase().includes(query) && 
            !ws.slug.toLowerCase().includes(query) &&
            !ws.ownerEmail?.toLowerCase().includes(query)) {
          return false;
        }
      }
      
      // Status filter
      if (filters.status && ws.status !== filters.status) return false;
      
      // Plan filter
      if (filters.plan && ws.plan !== filters.plan) return false;
      
      // Demo filter
      if (filters.demo && !ws.isDemo) return false;
      
      return true;
    });
  }, [workspaces, searchQuery, filters]);
  
  // Handle workspace actions (persist to DB)
  const handleAction = async (action: string, workspace: WorkspaceOverview) => {
    if (action === 'suspend') {
      try {
        await updateWorkspaceStatus(workspace.id, 'suspended');
        setWorkspaces(prev => prev.map(ws =>
          ws.id === workspace.id ? { ...ws, status: 'suspended' as const } : ws
        ));
      } catch (e) {
        console.error(e);
        alert(e instanceof Error ? e.message : 'Failed to suspend workspace');
      }
    } else if (action === 'activate') {
      try {
        await updateWorkspaceStatus(workspace.id, 'active');
        setWorkspaces(prev => prev.map(ws =>
          ws.id === workspace.id ? { ...ws, status: 'active' as const } : ws
        ));
      } catch (e) {
        console.error(e);
        alert(e instanceof Error ? e.message : 'Failed to activate workspace');
      }
    } else if (action === 'delete') {
      if (confirm(`Are you sure you want to delete "${workspace.name}"? This cannot be undone.`)) {
        try {
          await updateWorkspaceStatus(workspace.id, 'archived');
          setWorkspaces(prev => prev.filter(ws => ws.id !== workspace.id));
        } catch (e) {
          console.error(e);
          alert(e instanceof Error ? e.message : 'Failed to archive workspace');
        }
      }
    }
  };
  
  // Update filter and URL
  const setFilter = (key: FilterKey, value: string | boolean | undefined) => {
    const newFilters = { ...filters };
    if (value === undefined) {
      delete newFilters[key];
    } else {
      (newFilters as any)[key] = value;
    }
    setFilters(newFilters);
    
    // Update URL
    const newParams = new URLSearchParams();
    if (newFilters.status) newParams.set('status', newFilters.status);
    if (newFilters.plan) newParams.set('plan', newFilters.plan);
    if (newFilters.demo) newParams.set('filter', 'demo');
    setSearchParams(newParams);
  };
  
  const clearFilters = () => {
    setFilters({});
    setSearchParams({});
  };
  
  const hasActiveFilters = Object.keys(filters).length > 0;

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-[var(--hover-tint)] rounded w-48" />
          <div className="h-64 bg-[var(--hover-tint)]" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-pri)]">Workspaces</h1>
          <p className="text-[var(--text-ter)] mt-1">
            Manage all workspaces on the platform. Data from database.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 border border-[var(--rule)] hover:bg-[var(--surface)]"
          >
            <RefreshCcw className="h-4 w-4" />
            Refresh
          </button>
          <Link
            to="/platform-admin/workspaces/new"
            className="flex items-center gap-2 px-4 py-2 bg-[var(--coral)] text-white hover:bg-[var(--coral)] transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create Workspace
          </Link>
        </div>
      </div>

      {loadError && (
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-4 flex items-center justify-between">
          <span className="text-[var(--coral)]">{loadError}</span>
          <button
            type="button"
            onClick={loadData}
            className="shrink-0 px-3 py-1.5 bg-[var(--coral-dim)] text-[var(--coral)] hover:bg-[var(--coral-dim)]"
          >
            Retry
          </button>
        </div>
      )}
      
      {/* Search and Filters */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[var(--text-ter)]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search workspaces..."
            className="w-full pl-10 pr-4 py-2.5 border border-[var(--rule)] focus:ring-2 focus:ring-red-500 focus:border-[var(--coral)]"
          />
        </div>
        
        {/* Status Filter */}
        <div className="relative">
          <select
            value={filters.status || ''}
            onChange={(e) => setFilter('status', e.target.value || undefined)}
            className="appearance-none pl-4 pr-10 py-2.5 border border-[var(--rule)] bg-[var(--bg-raised)] focus:ring-2 focus:ring-red-500 focus:border-[var(--coral)]"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="demo">Demo</option>
            <option value="suspended">Suspended</option>
            <option value="archived">Archived</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)] pointer-events-none" />
        </div>
        
        {/* Plan Filter */}
        <div className="relative">
          <select
            value={filters.plan || ''}
            onChange={(e) => setFilter('plan', e.target.value || undefined)}
            className="appearance-none pl-4 pr-10 py-2.5 border border-[var(--rule)] bg-[var(--bg-raised)] focus:ring-2 focus:ring-red-500 focus:border-[var(--coral)]"
          >
            <option value="">All Plans</option>
            <option value="free">Free</option>
            <option value="starter">Starter</option>
            <option value="professional">Professional</option>
            <option value="enterprise">Enterprise</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)] pointer-events-none" />
        </div>
        
        {/* Demo Toggle */}
        <button
          onClick={() => setFilter('demo', filters.demo ? undefined : true)}
          className={`flex items-center gap-2 px-4 py-2.5 border  transition-colors
                     ${filters.demo 
                       ? 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]' 
                       : 'border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)]'}`}
        >
          <Sparkles className="h-4 w-4" />
          Demo Only
        </button>
        
        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)]"
          >
            Clear filters
          </button>
        )}
      </div>
      
      {/* Results Count */}
      <div className="text-sm text-[var(--text-ter)]">
        Showing {filteredWorkspaces.length} of {workspaces.length} workspaces
      </div>
      
      {/* Workspaces Grid */}
      {filteredWorkspaces.length === 0 ? (
        <div className="text-center py-12 bg-[var(--bg-raised)] border border-[var(--rule)]">
          <Building2 className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[var(--text-pri)] mb-1">No workspaces found</h3>
          <p className="text-[var(--text-ter)]">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWorkspaces.map(ws => (
            <WorkspaceCard 
              key={ws.id} 
              workspace={ws} 
              onAction={handleAction}
            />
          ))}
        </div>
      )}
    </div>
  );
}
